import { cloneDeep, findIndex, isEqual, isNumber, pullAll, toInteger } from 'lodash';
import {
    convertToLayerPoint,
    copyOriginIndex,
    getBehavior,
    getFramework,
    getStageIndexByMousePointY,
    getTrackFunc,
    HoverExtendType,
    IFramework,
    IHoverExtendArgs,
    isLayerProps,
    parseLayerId,
    resetGroupLayerRotate,
    resetLayer,
} from '@/framework';
import { Framework } from '@/framework/instance';
import { getRotatePosition } from '@/utils/collision-kit';
import { getLastMousePoint } from '@/utils/event-kit';
import { getLayerInfoWithRotate, isRectOutOfBounds } from '@/utils/layer-kit';
import { asyncTraverseTrees, traverseTrees } from '@/utils/tree-kit';
import {
    IExportPlugin,
    IExtraInfo,
    IImageEffectProps,
    LayerType,
    PluginType,
    type IImageLayerProps,
    type IImageTransform,
    type ILayerProps,
    type IPoint,
} from '@/version-types';
import ImageEffectAction from '../image-layer/utils/image-effect-action';
import { CanvasAlign } from './canvas-align';
import { GroupAlign } from './group-align';
import { LayerHistory } from './layer-history';
import { ModifyBasicProperties } from './modify-basic-properties';
import { HistoryType } from './type';

/** 添加图层参数 */
export interface IAddLayerArgs {
    /** 图层 */
    layer: ILayerProps;
    /** 是否激活新图层，默认true */
    isActiveNewLayer?: boolean;
    /** 是否写入历史纪录，默认true */
    isWriteHistory?: boolean;
    /** 统计值 */
    trackFunc?: string;
    /** 画布对应的额外信息 */
    extraInfo?: {
        oldExtraInfo: IExtraInfo;
        newExtraInfo: IExtraInfo;
    };
}

export class Layer extends Framework {
    /** 上一次添加的图层id */
    private preAddLayerId = '';

    /** 组内对齐 */
    groupAlign: GroupAlign;
    /** 画布对齐 */
    canvasAlign: CanvasAlign;
    /** 修改基础属性 */
    modifyBasicProperties: ModifyBasicProperties;

    constructor(identity: string) {
        super(identity);
        this.framework = getFramework(identity);
        this.groupAlign = new GroupAlign(this.framework);
        this.canvasAlign = new CanvasAlign(this.framework);
        this.modifyBasicProperties = new ModifyBasicProperties(this.framework, this.identity);
    }

    /** 强制更新图层 */
    forceUpdateLayer(layerId: string) {
        const layer = this.framework.getLayer(layerId, true);
        if (!layer) return;
        const newLayer = cloneDeep(layer);
        newLayer.__forceRenderKey = Date.now();
        this.framework.stage.addOrUpdate(newLayer);
    }

    /**
     * 解析图层ID
     * @param layerId - 图层ID
     * @returns 解析后的图层ID
     */
    parseLayerId(layerId: string) {
        return parseLayerId(layerId);
    }

    /**
     * 复制图层原始索引
     * @param layerId - 图层ID
     * @param fromLayerId - 来源图层ID
     * @returns 复制后的图层ID
     */
    copyOriginIndex(layerId: string, fromLayerId: string) {
        return copyOriginIndex(layerId, fromLayerId);
    }

    /**
     * 设置文本图层画笔
     */
    setTextBrush(layerId: string, applyFn: (layerId: string) => void, tip?: string) {
        const layer = this.framework.getLayer(layerId);
        if (!layer) return;
        this.framework.operate.hoverExtend.set({
            cursor: '--cursor-brush',
            layerId,
            type: HoverExtendType.TEXT_BRUSH,
            cursorTip: tip,
            apply: (id) => applyFn(id),
        });
    }

    /** 监听hover扩展 */
    onHoverExtend(callback: (args: IHoverExtendArgs | null) => void, weight = 0) {
        return this.framework.operate.hoverExtend.onChange(callback, weight);
    }

    /** 获取hover扩展 */
    getHoverExtend() {
        return this.framework.operate.hoverExtend.get();
    }

    /** 清除hover扩展 */
    clearHoverExtend() {
        this.framework.operate.hoverExtend.destroy();
    }

    /**
     * 设置图层透明度
     *
     * @param layerId - 图层 ID
     * @param opacity - 透明度
     * @param originOpacity - 原始透明度，传入这个参数时，需要写入历史纪录
     */
    setOpacity(layerId: string, opacity: number, originOpacity?: number | null) {
        const currentLayer = this.framework.getLayer(layerId);
        const newLayer = cloneDeep(currentLayer);
        newLayer.opacity = opacity;
        this.framework.stage.addOrUpdate(newLayer);
        if (isNumber(originOpacity)) {
            const oldLayer = cloneDeep(currentLayer);
            oldLayer.opacity = originOpacity;
            this.framework.history.write(oldLayer, getTrackFunc(this.identity, oldLayer, 'opacity'));
            this.framework.operate.setActiveLayer(newLayer.id);
        }
    }

    /**
     * 锁定图层
     *
     * @param layerId - 图层 ID
     */
    lock(layerId: string) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer) return;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'lock');
        const isExtraGroup = this.framework.checkIsExtraGroup(oldLayer);
        if (isExtraGroup) {
            const childIds = oldLayer.children?.map((item) => item.id);
            const res = this.framework.tryRemoveGroup(oldLayer);
            if (!res) return;
            for (let i = 0; i < childIds.length; i++) {
                const childId = childIds[i];
                const childLayer = this.framework.getLayer(childId, true);
                if (!childLayer) continue;
                childLayer.lock = true;
                this.framework.stage.addOrUpdate(childLayer, false);
            }
            this.framework.rerender();
            this.framework.history.write({
                type: HistoryType.LOCK,
                data: {
                    item: cloneDeep(oldLayer),
                },
                trackFunc,
            });
            this.framework.operate.delActiveLayer();
        } else {
            const newLayer = cloneDeep(oldLayer);
            newLayer.lock = true;
            this.framework.stage.addOrUpdate(newLayer);
            this.framework.history.write(oldLayer, trackFunc);
            this.framework.operate.setActiveLayer(newLayer.id);
        }
    }

    /**
     * 解锁图层
     *
     * @param layerId - 图层 ID
     */
    unlock(layerId: string) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer) return;
        const newLayer = cloneDeep(oldLayer);
        newLayer.lock = false;
        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write(oldLayer, getTrackFunc(this.identity, oldLayer, 'lock'));
        this.framework.operate.setActiveLayer(newLayer.id);
    }
    /**
     * 排序图层
     * @param operateCount - 操作次数
     * @param data - 排序数据
     * @param pushHistory - 是否写入历史纪录
     */
    sort(
        operateCount: number,
        data: {
            /** 原始索引 */
            originIndex: number;
            /** 排序列表 */
            sortList: { id: string; index: number }[];
            /** 子列表 */
            childList: { id: string; index: number }[];
        },
        pushHistory = true,
    ) {
        const sortList = cloneDeep(data.sortList);
        const childList = cloneDeep(data.childList);
        const oldSortList: { id: string; index: number }[] = [];
        if (pushHistory) {
            oldSortList.push(...[...cloneDeep(sortList), ...cloneDeep(childList)]);
            oldSortList.sort((a, b) => a.index - b.index);
            // console.log('sort oldSortList', oldSortList);
        }
        // 根据 sortList childList 里面的id 对应的 index 恢复 renderIds 的顺序
        const renderIds = this.framework.getRenderIds(data.originIndex);
        const originRenderIds = Array(renderIds.length).fill(undefined);
        const ids: string[] = [];
        for (let i = 0; i < sortList.length; i++) {
            const item = sortList[i];
            originRenderIds[item.index] = item.id;
            ids.push(item.id);
        }
        for (let i = 0; i < childList.length; i++) {
            const item = childList[i];
            originRenderIds[item.index] = item.id;
            ids.push(item.id);
        }
        const oldRenderIds = [...renderIds];
        pullAll(oldRenderIds, ids);
        for (let i = 0; i < renderIds.length; i++) {
            if (originRenderIds[i] === undefined) {
                const id = oldRenderIds.shift();
                if (id) {
                    originRenderIds[i] = id;
                }
            }
        }
        renderIds.length = 0;
        renderIds.push(...originRenderIds);

        // console.log('old', [...renderIds]);

        if (operateCount !== 0) {
            const newRenderIds = Array(renderIds.length).fill(undefined);
            let diffIndex = operateCount;

            let baseLastItem = childList[childList.length - 1];
            let baseFirstItem = childList[0];

            let index = 0;
            if (operateCount > 0) {
                for (let i = 0; i < childList.length; i++) {
                    const item = childList[i];
                    if (i === 0) {
                        index = item.index;
                    } else {
                        if (index + 1 !== item.index) break;
                        index = item.index;
                    }
                    baseFirstItem = item;
                }
            } else {
                for (let i = childList.length - 1; i >= 0; i--) {
                    const item = childList[i];
                    if (i === childList.length - 1) {
                        index = item.index;
                    } else {
                        if (index - 1 !== item.index) break;
                        index = item.index;
                    }
                    baseLastItem = item;
                }
            }

            if (operateCount < 0) {
                const target = baseLastItem; // childList[childList.length - 1];
                const source: { id: string; index: number }[] = [...sortList, target];
                source.sort((a, b) => a.index - b.index);
                const targetIndex = findIndex(source, (item) => item.id === target.id);
                diffIndex = source[targetIndex + operateCount].index - source[targetIndex].index;
                // console.log(
                //     'firstIndex 0 => ',
                //     source[targetIndex + operateCount].index,
                //     source[targetIndex].index,
                // );
            } else {
                const target = baseFirstItem; // childList[0];
                const source: { id: string; index: number }[] = [...sortList, target];
                source.sort((a, b) => a.index - b.index);
                const targetIndex = findIndex(source, (item) => item.id === target.id);
                diffIndex = source[targetIndex + operateCount].index - source[targetIndex].index;
            }
            // console.log('firstIndex 1 => ', { diffIndex });

            const childFirstIndex = baseFirstItem.index; // childList[0].index;
            const childLastIndex = baseLastItem.index; // childList[childList.length - 1].index;

            const ids: string[] = [];
            for (let i = 0; i < childList.length; i++) {
                const item = childList[i];
                item.index += diffIndex;
                ids.push(item.id);
            }
            // console.log('2 => ', { diffIndex, childList: cloneDeep(childList) });

            if (operateCount < 0) {
                let firstIndex = Math.min(sortList[0].index, childFirstIndex);
                // console.log('firstIndex 2 =>', firstIndex, { s: sortList[0].index, childFirstIndex, childLastIndex });

                for (let i = 0; i < childList.length; i++) {
                    const item = childList[i];
                    if (item.index < firstIndex) {
                        item.index = firstIndex;
                    }
                    firstIndex = item.index + 1;
                }
            } else {
                let lastIndex = Math.max(sortList[sortList.length - 1].index, childLastIndex);
                for (let i = childList.length - 1; i >= 0; i--) {
                    const item = childList[i];
                    if (item.index > lastIndex) {
                        item.index = lastIndex;
                    }
                    lastIndex = item.index - 1;
                }
            }
            for (let i = 0; i < childList.length; i++) {
                const item = childList[i];
                newRenderIds[item.index] = item.id;
            }

            const oldRenderIds = [...renderIds];
            pullAll(oldRenderIds, ids);

            // console.log('diffIndex', {
            //     childList: [...childList],
            //     newRenderIds: [...newRenderIds],
            //     diffIndex,
            //     operateCount,
            //     oldRenderIds: [...oldRenderIds],
            //     ids: [...ids],
            // });

            for (let i = 0; i < renderIds.length; i++) {
                if (newRenderIds[i] === undefined) {
                    const id = oldRenderIds.shift();
                    if (id) {
                        newRenderIds[i] = id;
                    }
                }
            }
            renderIds.length = 0;
            renderIds.push(...newRenderIds);
        }

        // console.log('new', [...renderIds]);
        this.framework.setRerender(data.originIndex);
        this.framework.rerender();

        if (pushHistory) {
            const newSortList = oldSortList.map((item) => {
                return {
                    ...item,
                    index: renderIds.indexOf(item.id),
                };
            });
            this.framework.history.write({
                type: HistoryType.SORT,
                data: {
                    originIndex: data.originIndex,
                    oldSortList,
                    newSortList,
                },
                needSyncRenderIds: true,
            });
        }
    }

    /**
     * 获取图层排序数据
     * @param layerId - 图层ID
     * @param ignoreTypes - 忽略的图层类型
     * @returns 排序数据
     */
    getSortData(layerId: string, ignoreTypes?: LayerType[]) {
        return this.framework.getLayerSortData(layerId, ignoreTypes);
    }

    // 非图层组翻转
    private async flipLayer(layer: ILayerProps, isFlipH: boolean, isFlipV: boolean) {
        const data = layer.data as IImageLayerProps;
        if (data?.imageTransform) {
            const imageTransform = data.imageTransform as IImageTransform;
            if (isFlipH) {
                imageTransform.flipH = !imageTransform.flipH;
            }
            if (isFlipV) {
                imageTransform.flipV = !imageTransform.flipV;
            }
        } else {
            if (isFlipH) {
                layer.flipH = !layer.flipH;
            }
            if (isFlipV) {
                layer.flipV = !layer.flipV;
            }
        }
        if (layer.layerType === LayerType.IMAGE_LAYER) {
            const effectScreenshotUrl = (layer.data as IImageLayerProps).effect?.screenshotUrl;
            if (effectScreenshotUrl) {
                const layerResultImage = await await getBehavior(this.identity, layer)?.getLayerImageAsync?.(
                    layer,
                    false,
                    false,
                    true,
                    false,
                );
                if (layerResultImage) {
                    const effectUrl = await ImageEffectAction.runEffectAndGetUrl(layer, layerResultImage);
                    if (effectUrl && (layer.data as IImageLayerProps).effect) {
                        ((layer.data as IImageLayerProps).effect as IImageEffectProps).screenshotUrl =
                            effectUrl as string;
                    }
                }
            }
        }
    }

    /**
     * 水平/垂直翻转
     * @param id - 图层 ID
     * @param isFlipH - 是否水平翻转
     * @param isFlipV - 是否垂直翻转
     * @returns
     */
    private async flip(id: string, isFlipH: boolean, isFlipV: boolean) {
        let oldLayer = this.framework.getLayer(id, true);
        if (!oldLayer) return;
        const disableFlip = this.framework.getBehavior(oldLayer)?.checkDisableFlip?.(oldLayer);
        if (disableFlip) return;
        const activePickLayer = this.framework.operate.getActivePickLayer();
        if (this.framework.checkIsGroup(oldLayer) && activePickLayer && activePickLayer.id !== id) {
            oldLayer = this.framework.getLayer(activePickLayer.id, true);
        }
        const layer = cloneDeep(oldLayer);

        const resetLayer = (layer: ILayerProps) => {
            const positions: IPoint[] = [];
            (layer.children || []).forEach((item: ILayerProps) => {
                const position = getRotatePosition(item);
                positions.push(...position);
            });
            if (!positions.length) return;
            resetGroupLayerRotate(layer, positions);
        };

        if (this.framework.checkIsGroup(layer)) {
            // 图层组翻转需要对子图层进行重新计算
            const { left: groupLeft, top: groupTop, width: groupWidth, height: groupHeight } = layer;
            // 子组需要重置大小
            const groupLayers: ILayerProps[] = [];
            await asyncTraverseTrees([layer], async (layer: ILayerProps) => {
                const { left: layerLeft, top: layerTop, width: layerWidth, height: layerHeight } = layer;
                const width = layerWidth;
                const height = layerHeight;
                if (layer.children?.length > 0) {
                    groupLayers.push(layer);
                    return;
                }
                if (isFlipH) {
                    // 主图中心点和舞台的相对位置
                    const relativeStagePoSition = {
                        x: layerLeft + width / 2 - groupWidth / 2 - groupLeft,
                        y: layerTop + height / 2 - groupHeight / 2,
                    };
                    const flipXPosition = {
                        x: -relativeStagePoSition.x,
                        y: relativeStagePoSition.y,
                    };
                    const newPosition = {
                        x: flipXPosition.x + groupLeft + groupWidth / 2 - width / 2,
                        y: flipXPosition.y + groupHeight / 2 - height / 2,
                    };
                    layer.rotate = layer.rotate + (360 - layer.rotate * 2);
                    layer.left = newPosition.x;
                    layer.top = newPosition.y;
                }
                if (isFlipV) {
                    // 主图中心点和舞台的相对位置
                    const relativeStagePoSition = {
                        x: layerLeft + width / 2 - groupWidth / 2,
                        y: layerTop + height / 2 - groupHeight / 2 - groupTop,
                    };
                    const flipYPosition = {
                        x: relativeStagePoSition.x,
                        y: -relativeStagePoSition.y,
                    };
                    const newPosition = {
                        x: flipYPosition.x + groupWidth / 2 - width / 2,
                        y: flipYPosition.y + groupTop + groupHeight / 2 - height / 2,
                    };
                    layer.rotate = -layer.rotate;
                    layer.left = newPosition.x;
                    layer.top = newPosition.y;
                }
                await this.flipLayer(layer, isFlipH, isFlipV);
            });
            for (let i = groupLayers.length - 1; i > 0; i--) {
                const item = groupLayers[i];
                resetLayer(item);
            }
        } else {
            // 非图层组翻转
            await this.flipLayer(layer, isFlipH, isFlipV);
        }

        return {
            oldLayer,
            newLayer: layer,
        };
    }

    /**
     * 水平翻转
     *
     * @param layerId - 图层 ID
     */
    async flipX(layerId: string) {
        const res = await this.flip(layerId, true, false);
        if (!res) return;
        const { oldLayer, newLayer } = res;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'flipH');
        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write(oldLayer, newLayer, trackFunc);
        this.framework.operate.setActiveLayer(newLayer.id);
    }

    /**
     * 垂直翻转
     *
     * @param layerId - 图层 ID
     */
    async flipY(layerId: string) {
        const res = await this.flip(layerId, false, true);
        if (!res) return;
        const { oldLayer, newLayer } = res;
        const trackFunc = getTrackFunc(this.identity, oldLayer, 'flipV');
        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write(oldLayer, newLayer, trackFunc);
        this.framework.operate.setActiveLayer(newLayer.id);
    }

    /**
     * 隐藏图层
     *
     * @param layerId - 图层 ID
     * @param isHidden - 是否隐藏
     * @param autoActive - 是否自动设置激活图层
     */
    setHidden(layerId: string, isHidden: boolean, autoActive = false) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer || oldLayer.hidden === isHidden) return;
        const newLayer = cloneDeep(oldLayer);
        newLayer.hidden = isHidden;
        this.framework.stage.addOrUpdate(newLayer);
        this.framework.history.write(oldLayer);
        if (autoActive) {
            this.framework.operate.setActiveLayer(newLayer.id);
        }
    }

    /**
     * 克隆图层
     *
     * @param layerId - 图层 ID
     */
    clone(layerId: string) {
        const oldLayer = this.framework.getLayer(layerId, true);
        if (!oldLayer) return;
        const disableClone = this.framework.getBehavior(oldLayer)?.checkDisableClone?.(oldLayer);
        if (disableClone) return;
        resetLayer(oldLayer);
        this.addLayer(
            {
                layer: oldLayer,
                isActiveNewLayer: true,
                isWriteHistory: true,
                trackFunc: getTrackFunc(this.identity, oldLayer, 'clone'),
            },
            'diagonal',
            20,
            this.preAddLayerId || layerId,
        );
    }

    /**
     * 获取图层
     * @param layerId - 图层 ID
     * @param withClone - 是否克隆
     * @returns 图层
     */

    getLayer(layerId: string, withClone = false) {
        return this.framework.getLayer(layerId, withClone);
    }

    /** 重置preAddLayerId */
    resetPreAddLayerId(id?: string) {
        this.preAddLayerId = id || '';
    }

    /**
     * 删除图层
     *
     * @param layerId - 图层 ID
     * @param prevLayer - 删除时的图层数据
     */
    delete(layerId: string, prevLayer?: ILayerProps) {
        const layer = prevLayer || this.framework.getLayer(layerId);
        if (!layer || this.framework.checkIsNeedLock(layerId)) return;
        if (!this.framework.checkPossibleDelete(layer)) return;
        const originLayer = cloneDeep(this.framework.getWrapLayer(layerId, true));
        // FIXME: 调整删除图层逻辑
        if (prevLayer && originLayer) {
            if (prevLayer.id === originLayer.id) {
                Object.assign(originLayer, prevLayer);
            } else if (originLayer.children) {
                traverseTrees(originLayer.children, (child: ILayerProps) => {
                    if (child.id === prevLayer.id) {
                        Object.assign(child, prevLayer);
                    }
                });
            }
        }
        const historyData = this.framework.operate.delLayer(layerId);
        if (historyData) {
            if (prevLayer) {
                if (prevLayer.id === historyData.item.oldLayer.id) {
                    Object.assign(historyData.item.oldLayer, prevLayer);
                } else if (historyData.item.oldLayer.children) {
                    traverseTrees(historyData.item.oldLayer.children, (child: ILayerProps) => {
                        if (child.id === prevLayer.id) {
                            Object.assign(child, prevLayer);
                        }
                    });
                }
            }
            this.framework.history.write({
                type: HistoryType.DELETE,
                data: {
                    historyData,
                    originLayer,
                },
                trackFunc: getTrackFunc(this.identity, layer, 'delete'),
                needSyncRenderIds: true,
            });
            const activeId = historyData.operate.activeId;
            if (activeId) {
                this.framework.operate.setActiveLayer(activeId);
            } else {
                this.framework.operate.delActiveLayer();
            }
        }
    }

    /**
     * 添加图层, 斜向偏移-向右下角偏移
     *
     * @param layerId - 图层 ID
     */
    addByDiagonal(layer: ILayerProps | IAddLayerArgs) {
        return this.addLayer(layer, 'diagonal', 20);
    }

    /**
     * 添加图层, 竖向偏移-向下方偏移
     *
     * @param layerId - 图层 ID
     */
    addByStraight(layer: ILayerProps | IAddLayerArgs) {
        return this.addLayer(layer, 'straight');
    }

    /**
     * 根据鼠标位置添加图层
     *
     * @param layer - 图层属性
     */
    addByMousePosition(layer: ILayerProps | IAddLayerArgs) {
        const mousePoint = getLastMousePoint();
        return this.addByDocPoint(layer, mousePoint);
    }

    /**
     * 根据文档坐标添加图层
     *
     * @param layer - 图层属性
     * @param docPoint - 文档坐标
     */
    addByDocPoint(layer: ILayerProps | IAddLayerArgs, docPoint: IPoint) {
        const isLongImageMode = this.framework.stage.isLongImageMode();
        let stagePoint: IPoint;
        const stageIndex = this.framework.stage.getActiveStageIndex();
        if (isLongImageMode) {
            const currentStageIndex = getStageIndexByMousePointY(this.identity, docPoint.y);
            if (currentStageIndex !== stageIndex) {
                this.framework.stage.setActiveStageIndex(currentStageIndex);
            }
            stagePoint = convertToLayerPoint(this.identity, docPoint, currentStageIndex);
        } else {
            stagePoint = convertToLayerPoint(this.identity, docPoint, stageIndex);
        }
        return this.addByStagePoint(layer, stagePoint);
    }

    /**
     * 根据舞台坐标添加图层
     *
     * @param layer - 图层属性
     * @param stagePoint - 舞台坐标
     */
    addByStagePoint(layer: ILayerProps | IAddLayerArgs, stagePoint: IPoint) {
        const currentLayer = isLayerProps(layer) ? layer : layer.layer;
        const { left, top } = currentLayer;

        currentLayer.left = toInteger(stagePoint.x - currentLayer.width / 2);
        currentLayer.top = toInteger(stagePoint.y - currentLayer.height / 2);

        if (currentLayer.children?.length) {
            const changeLeft = currentLayer.left - left;
            const changeTop = currentLayer.top - top;

            traverseTrees(currentLayer.children, (child: ILayerProps) => {
                child.left = toInteger(child.left + changeLeft);
                child.top = toInteger(child.top + changeTop);
            });
        }

        return this.addLayer(currentLayer);
    }

    /**
     * 添加图层
     *
     * @param layer - 图层属性
     */
    add(layer: ILayerProps | IAddLayerArgs) {
        return this.addLayer(layer);
    }

    /**
     * 添加图层
     *
     * @param layer - 图层属性
     * @param deviationMode - 偏移模式 diagonal: 斜向偏移 straight: 竖向偏移
     * @param offset - 斜向偏移的偏移量，竖向每次偏移上一个图层的高度
     */
    private addLayer(
        layer: ILayerProps | IAddLayerArgs,
        deviationMode?: 'diagonal' | 'straight',
        offset = 0,
        preAddLayerId = this.preAddLayerId,
    ) {
        if (deviationMode) {
            const currentLayer = isLayerProps(layer) ? layer : layer.layer;
            const newPosition = getAddLayerPosition(this.framework, currentLayer, offset, deviationMode, preAddLayerId);
            // console.log(
            //     'currentLayer',
            //     {
            //         left: currentLayer.left,
            //         top: currentLayer.top,
            //     },
            //     `preAddLayerId: ${preAddLayerId}`,
            // );
            if (currentLayer.children?.length) {
                const offsetX = newPosition.left - currentLayer.left;
                const offsetY = newPosition.top - currentLayer.top;
                resetLayer(currentLayer, offsetX, offsetY);
            } else {
                currentLayer.left = newPosition.left;
                currentLayer.top = newPosition.top;
                // console.log('newPosition', {
                //     left: currentLayer.left,
                //     top: currentLayer.top,
                // });
            }
        }
        const isLayer = isLayerProps(layer);
        let isActiveNewLayer = true;
        let isWriteHistory = true;
        let trackFunc = '';
        let extraInfo:
            | {
                  oldExtraInfo: IExtraInfo;
                  newExtraInfo: IExtraInfo;
              }
            | undefined;
        if (!isLayer) {
            const data = layer as unknown as IAddLayerArgs;
            isActiveNewLayer = data.isActiveNewLayer ?? true;
            isWriteHistory = data.isWriteHistory ?? true;
            layer = data.layer;
            trackFunc = data.trackFunc ?? getTrackFunc(this.identity, data.layer, 'add');
            extraInfo = data.extraInfo;
        } else {
            trackFunc = getTrackFunc(this.identity, layer as ILayerProps, 'add');
        }
        const resLayer = this.framework.stage.addOrUpdate(layer as ILayerProps);
        if (extraInfo?.newExtraInfo) {
            this.framework.setExtraInfoByLayerId(resLayer.id, extraInfo.newExtraInfo);
        }
        if (isWriteHistory) {
            const currentActiveInfo = this.framework.operate.getActiveInfo();
            this.framework.history.write({
                type: HistoryType.ADD,
                data: {
                    item: cloneDeep(resLayer),
                    extraInfo,
                    operate: {
                        oldActiveId: currentActiveInfo?.info.pickId || currentActiveInfo?.info.id,
                        isActiveNewLayer,
                    },
                },
                trackFunc,
                needSyncRenderIds: true,
            });
        }
        if (isActiveNewLayer) {
            this.framework.operate.setActiveLayer(resLayer.id);
        } else {
            this.framework.operate.delActiveLayer();
        }
        if (deviationMode) {
            this.preAddLayerId = resLayer.id;
        }
        this.framework.createFontFamilySubset([resLayer], true);
        return resLayer;
    }

    /**
     * 检查图层是否可以调整宽高
     * @param layer - 图层属性
     * @returns 是否可以调整宽高
     */
    checkCanResizeOperate = (layer: ILayerProps) => {
        return this.framework.checkCanResizeOperate(layer);
    };

    /**
     * 检查图层是否默认不等比拉伸
     * @param layerType - 图层类型
     * @returns 是否默认不等比拉伸
     */
    checkIsDefaultUnequalStretch = (layer: ILayerProps) => {
        return getBehavior(this.identity, layer)?.isDefaultUnequalStretch?.() ?? false;
    };

    /**
     * 遍历图层及其所有父级图层的属性是否为指定值
     *
     * @param layerId - 图层 ID
     * @param key - 属性键值, 可为 key1.key2.key3... 格式
     * @param value - 属性值
     * @returns Boolean
     */
    checkWrapLayerProps = (layerId: string, key: string, value: any): boolean => {
        const layer = this.framework.getLayer(layerId);

        if (!layer) return false;

        const getValue = (layer: ILayerProps): any => {
            const keys = key.split('.');
            return keys.reduce((acc, key) => acc[key], layer);
        };

        const checkLayerProps = (layer: ILayerProps) => {
            if (isEqual(getValue(layer), value)) {
                return true;
            }
            if (layer.parentId) {
                return checkLayerProps(this.framework.getLayer(layer.parentId));
            }
            return false;
        };

        return checkLayerProps(layer);
    };
}

/**
 * 获取添加图层的坐标
 *
 * @param framework - 编辑器框架实例对象
 * @param layer - 图层属性
 * @param offset - 偏移量
 * @param deviationMode - 偏移模式 diagonal: 斜向偏移 straight: 竖向偏移
 * @param preLayerId - 之前的图层 ID
 */
function getAddLayerPosition(
    framework: IFramework,
    layer: ILayerProps,
    offset: number,
    deviationMode: 'diagonal' | 'straight',
    preLayerId?: string,
) {
    const stageSize = framework.stage.getActiveStageSize();

    if (offset) {
        // 按照 1242px 的宽度计算偏移量
        offset = Math.ceil(stageSize.width / (1242 / offset));
    }

    const stageCenterPosition = {
        left: (stageSize.width - layer.width) / 2,
        top: (stageSize.height - layer.height) / 2,
    };
    if (!preLayerId) return stageCenterPosition;

    const preLayer = framework.getLayer(preLayerId);
    if (!preLayer) return stageCenterPosition;

    // 上一个图层不在当前激活画布内，返回中心位置
    const preLayerOriginIdex = framework.tryGetLayerOriginIndex(preLayerId);
    const activeOriginIndex = framework.stage.getActiveOriginIndex();
    if (preLayerOriginIdex !== activeOriginIndex) return stageCenterPosition;

    // 斜向偏移
    const newPosition = {
        left: preLayer.left + offset,
        top: preLayer.top + offset,
    };
    // 竖向偏移, 每次偏移一个图层的高度
    if (deviationMode === 'straight') {
        newPosition.left = stageCenterPosition.left;
        newPosition.top = preLayer.top + preLayer.height;
    }

    // 计算图层是否在舞台中
    const newLayerInfo = getLayerInfoWithRotate({
        ...newPosition,
        width: layer.width,
        height: layer.height,
        rotate: layer.rotate,
    });
    if (!isRectOutOfBounds(newLayerInfo, stageSize)) {
        return newPosition;
    }

    return stageCenterPosition;
}

/**
 * 基础图层插件
 */
export const layerPlugin: IExportPlugin = {
    type: PluginType.EXTENSION,
    sort: 0,
    name: 'base-layer',
    createInstance({ identity }) {
        return [{ key: 'Layer', value: new Layer(identity) }];
    },
    createHistory({ identity }) {
        return [new LayerHistory(identity)];
    },
};
